import { useState, useRef, useEffect, useContext, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Input,
  Box,
  TextField,
  MenuItem,
  Select,
} from "@material-ui/core";
import Timer from "./Timer";
import { ResizableBox } from "react-resizable";
import useWindowSize from "../utils/window-size";
import "../Styles/resizable.css";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import SortableTableHeader from "./SortableTableHeader";
import {
  numberSort,
  wordSort,
  dateSort,
  useLocalStorage,
  localGetData,
  localStoreData,
} from "../utils/utils";
import TableColumnShowHide from "./TableColumnShowHide";
import { DashboardContext } from "../Contexts/DashboardContext";
import { AVAILABLE_OFFICER_ACTIONS } from "../Actions/AvailableOfficerActions";
import { useCookies } from "react-cookie";
import { useStyles } from "../Styles/styles";
import { withStyles } from "@material-ui/core/styles";
import { SetupContext } from "../Contexts/SetupContext";
import _ from "lodash";
//Mutations
import { useMutation } from "@apollo/client";
import { SET_STATUS } from "../Mutations/SetStatus";
import { UserContext } from "../Contexts/UserContext";
import HelpIconButton from "./HelpIconButton";
import { canEditAvailableOfficers, isDispatcher } from "../utils/Roles";
import { SET_LAST_CONTACTED } from "../Mutations/SetLastContacted";
import RadioLogModal from "./RadioLogModal";
import { useToggle } from "../CustomHooks/useToggle";
import { getOriColor } from "../Styles/getOriColor";

import moment from "moment";

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const StyledTableCell = withStyles(() => ({
  root: {
    borderRight: "2px solid #e3e2e2",
    textAlign: "center",
    padding: 0,
  },
}))(TableCell);

const SORT = {
  badge: {
    path: "deptAttribute.badge",
    fnc: numberSort,
  },
  carNumber: {
    path: "deptAttribute.carNumber",
    fnc: numberSort,
  },
  radioNumber: {
    path: "deptAttribute.radioNumber",
    fnc: numberSort,
  },
  lastContactedAt: {
    path: "deptAttribute.lastContactedAt",
    fnc: dateSort,
  },
  ori: {
    path: "deptAttribute.ori.abbreviation",
    fnc: wordSort,
  },
  assignment: {
    path: "deptAttribute.assignment",
    fnc: wordSort,
  },
  location: {
    path: "deptAttribute.officerLocation",
    fnc: wordSort,
  },
  skills: {
    path: "deptAttribute.skillsList",
    fnc: wordSort,
  },
  notes: {
    path: "deptAttribute.notes",
    fnc: wordSort,
  },
  status: {
    path: "deptAttribute.status",
    fnc: numberSort,
  },
  officerTimer: {
    path: "deptAttribute.lastContactedAt",
    fnc: dateSort,
  },
};

const TABLE_NAME = "availableOfficer";
const AvailableOfficerTable = ({
  dashSize,
  loading,
  error,
  user,
  setAvailOfficer,
  availableOfficersCalled,
  userPreferences,
}) => {
  const { availableOfficers, availableOfficerDispatch } =
    useContext(DashboardContext);
  const { setUser, onlineState } = useContext(UserContext);
  const { setup } = useContext(SetupContext);
  const { UPDATE_AVAILABLE_OFFICER, SET_STATE } = AVAILABLE_OFFICER_ACTIONS;
  const [selectedCell, setSelectedCell] = useState("");
  const classes = useStyles();
  const [setStatus] = useMutation(SET_STATUS);
  const sortLabel = useRef();
  const [sortDirection, setSortDirection] = useState(
    localGetData(`${user?.id}-table-sorting`)?.[TABLE_NAME]?.direction || null
  );
  const [sortedColumn, setSortedColumn] = useState(
    localGetData(`${user?.id}-table-sorting`)?.[TABLE_NAME]?.sortedColumn ||
      null
  );
  const [sortedId, setSortedId] = useState(
    localGetData(`${user?.id}-table-sorting`)?.[TABLE_NAME]?.id || null
  );
  const [showRadioModal, toggleRadioModal] = useToggle(false);
  const [selectedOfficer, setSelectedOfficer] = useState({});
  const windowSize = useWindowSize();

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!showRadioModal) {
      setSelectedOfficer({});
    }
  }, [showRadioModal]);

  function createData(
    officerId,
    abbreviation,
    deptId,
    badge,
    assignment,
    loanerOriAbbreviations,
    carNumber,
    radioNumber,
    officerLocation,
    skillsList,
    notes,
    lastContactedAt,
    status,
    officerOriId
  ) {
    return {
      officerId,
      abbreviation,
      deptId,
      badge,
      assignment,
      loanerOriAbbreviations,
      carNumber,
      radioNumber,
      officerLocation,
      skillsList,
      notes,
      lastContactedAt,
      status,
      officerOriId,
    };
  }

  const handleSetAvailOfficer = (e, row) => {
    const name = e.target.name;
    const value = e.target.value;
    const mutationName =
      e.target.name === "officerLocation" ? "secondaryAddress" : e.target.name;
    const localDate = moment().utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
    setAvailOfficer({
      variables: {
        input: {
          originatorId: user?.id,
          officerId: row.officerId,
          [mutationName]: e.target.value,
        },
      },
    });
    setSelectedCell("");
    availableOfficerDispatch({
      type: UPDATE_AVAILABLE_OFFICER,
      payload: { officerId: row.officerId, property: name, value: value },
    });
    availableOfficerDispatch({
      type: UPDATE_AVAILABLE_OFFICER,
      payload: {
        officerId: row.officerId,
        property: "lastContactedAt",
        value: localDate,
      },
    });
  };

  const handleStatusChange = (value, officerId) => {
    const localDate = moment().utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
    const tenCode = value?.name?.substring(0, value?.name?.indexOf("/"));
    availableOfficerDispatch({
      type: UPDATE_AVAILABLE_OFFICER,
      payload: {
        officerId: officerId,
        property: "lastContactedAt",
        value: localDate,
      },
    });
    availableOfficerDispatch({
      type: UPDATE_AVAILABLE_OFFICER,
      payload: { officerId: officerId, property: "status", value: tenCode },
    });
    availableOfficerDispatch({
      type: UPDATE_AVAILABLE_OFFICER,
      payload: { officerId: officerId, property: "officerLocation", value: "" },
    });
    setStatus({
      variables: {
        input: {
          originatorId: user?.id,
          officerId: officerId,
          logCodeId: value?.id,
          statusCode: tenCode,
        },
      },
      context: { serializationKey: "incidentQueue" },
    }).then(res => {
      const resTenCode = res?.data?.setStatus?.officer?.deptAttribute?.status;
      const responseOfficerId = res?.data?.setStatus?.officer?.id;
      if (responseOfficerId != null && resTenCode != null) {
        availableOfficerDispatch({
          type: UPDATE_AVAILABLE_OFFICER,
          payload: {
            officerId: responseOfficerId,
            property: "status",
            value: resTenCode,
          },
        });
      }
    });
    setAvailOfficer({
      variables: {
        input: {
          originatorId: user?.id,
          officerId: officerId,
          secondaryAddress: "",
        },
      },
    });
    if (
      value?.name?.startsWith("42") &&
      officerId.toString() === user?.id.toString()
    ) {
      let localOfficer = _.cloneDeep(user?.officer);
      localOfficer.deptAttribute.onDuty = false;
      let newOfficers = availableOfficers.filter(o => o?.id !== officerId);
      availableOfficerDispatch({ type: SET_STATE, payload: newOfficers });
      setUser(prevState => {
        return { ...prevState, onDuty: false, officer: localOfficer };
      });
    }
  };

  const [setLastContacted] = useMutation(SET_LAST_CONTACTED, {
    context: { serializationKey: "incidentQueue" },
  });

  const handleSetLastContactedAt = (officerId, row) => {
    const allowedToEdit = canEditAvailableOfficers(
      user,
      row?.officerOriId,
      row?.officerId
    );
    if (allowedToEdit) {
      const now = moment().format();
      const localDate =
        moment(now).utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
      const serverTimeFormat = moment.utc().format("YYYY-MM-DD HH:mm:ss UTC");
      setLastContacted({
        variables: {
          input: {
            officerId: officerId,
            lastContactedAt: serverTimeFormat,
          },
        },
      });
      availableOfficerDispatch({
        type: UPDATE_AVAILABLE_OFFICER,
        payload: {
          officerId: officerId,
          property: "lastContactedAt",
          value: localDate,
        },
      });
    } else {
      console.log(
        `You don't have permission to do that. OfficerId: ${officerId}. UserId: ${user?.id}.`
      );
    }
    return null;
  };

  // const handleSort = (event, columnName, id, sortLabel) => {
  //   const currentDirection = sortDirection;
  //   let newDirection;
  //   setSortedColumn(columnName)
  //   setSortedId(id)
  //   if (currentDirection === 'asc') {
  //     setSortDirection('desc')
  //     newDirection = 'desc'
  //   } else {
  //     setSortDirection('asc')
  //     newDirection = 'asc'
  //   }
  //   sortRows(newDirection, id, sortLabel)
  // }
  const handleSort = async (event, columnName, id, sortLabel) => {
    const currentDirection = sortDirection;
    let newDirection = null;
    if (currentDirection === "asc") {
      setSortDirection("desc");
      setSortedColumn(columnName);
      setSortedId(id);
      newDirection = "desc";
    } else if (currentDirection === "desc") {
      setSortDirection(null);
      setSortedColumn(null);
      setSortedId(null);
      id = null;
      sortLabel = null;
      columnName = null;
    } else {
      setSortDirection("asc");
      setSortedColumn(columnName);
      setSortedId(id);
      newDirection = "asc";
    }
    sortRows(newDirection, id, sortLabel);
    let storedTableSort = localGetData(`${user?.id}-table-sorting`) || {};
    storedTableSort[TABLE_NAME] = {
      direction: newDirection,
      id: id,
      sortedColumn: columnName,
    };
    await localStoreData(storedTableSort, `${user?.id}-table-sorting`);
  };

  const parseAvailableOfficers = useCallback(
    officers => {
      const onDutyOfficers = officers?.filter(o => o?.deptAttribute?.onDuty);
      let tempRows = [];
      onDutyOfficers.map(officer => {
        const { id: officerId } = officer;
        const {
          id: deptId,
          badge,
          assignment,
          loanerOriAbbreviations,
          carNumber,
          radioNumber,
          officerLocation,
          notes,
          skillsList,
          lastContactedAt,
          status,
        } = officer?.deptAttribute || {};
        const { abbreviation, id: officerOriId } =
          officer?.deptAttribute?.ori || {};
        if (officer?.id.toString() === user?.id.toString()) {
          tempRows.unshift(
            createData(
              officerId,
              abbreviation,
              deptId,
              badge,
              assignment,
              loanerOriAbbreviations,
              carNumber,
              radioNumber,
              officerLocation,
              skillsList,
              notes,
              lastContactedAt,
              status,
              officerOriId
            )
          );
        } else {
          tempRows.push(
            createData(
              officerId,
              abbreviation,
              deptId,
              badge,
              assignment,
              loanerOriAbbreviations,
              carNumber,
              radioNumber,
              officerLocation,
              skillsList,
              notes,
              lastContactedAt,
              status,
              officerOriId
            )
          );
        }
        return null;
      });
      return tempRows;
    },
    [user?.id]
  );

  const oriOnTop = (arr, oriId) => {
    let result = [];
    arr.map(officer =>
      officer?.deptAttribute?.ori?.id?.toString() === oriId.toString()
        ? result.unshift(officer)
        : result.push(officer)
    );
    return result;
  };

  const sortRows = useCallback(
    (direction, id) => {
      let data = availableOfficers;
      if (SORT[id]) {
        setRows(
          parseAvailableOfficers(
            SORT[id].fnc(direction, id, data, SORT[id].path)
          )
        );
      } else {
        setRows(parseAvailableOfficers(oriOnTop(data, user?.homeOriId)));
      }
    },
    [availableOfficers, parseAvailableOfficers, user?.homeOriId]
  );

  const activeOfficerColumnView = {
    badge: true,
    ori: true,
    assignment: true,
    location: true,
    skills: true,
    notes: true,
    carNumber: true,
    radioNumber: true,
    status: true,
    officerTimer: true,
  };
  const [showColumn, setShowColumn] = useState(activeOfficerColumnView);

  const handleShowColumn = e => {
    const name = e.target.value;
    const bool = e.target.checked;
    const newState = { ...showColumn, [name]: bool };

    setShowColumn(newState);

    const storeData = async () => {
      try {
        const res = await localStoreData(
          newState,
          `${user?.id}-available-officer-table-column-view`,
          false
        );
        return res;
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    storeData();
  };

  useEffect(() => {
    let data = localGetData(`${user?.id}-available-officer-table-column-view`);
    if (data) {
      setShowColumn(data);
    }
  }, [user?.id]);

  useEffect(() => {
    // window.localStorage.removeItem(
    //   `${user?.id}-available-officer-table-column-view`
    // );
    // const storeData = async () => {
    //   try {
    //     const res = await localStoreData(
    //       showColumn,
    //       `${user?.id}-available-officer-table-column-view`,
    //       false
    //     );
    //     return res;
    //   } catch (error) {
    //     console.error("Error fetching data:", error);
    //   }
    // };
    // storeData();
  }, [showColumn, user?.id]);

  useEffect(() => {
    if (availableOfficers && !loading && !error) {
      sortRows(sortDirection, sortedId);
    }
  }, [availableOfficers, error, loading, sortDirection, sortRows, sortedId]);

  const availableOfficersHeader = [
    { title: "Badge", idName: "badge", show: showColumn.badge },
    { title: "ORI", idName: "ori", show: showColumn.ori },
    { title: "Assign/Loan", idName: "assignment", show: showColumn.assignment },
    { title: "Ofc. Status", idName: "status", show: showColumn.status },
    { title: "Ofc. Location", idName: "location", show: showColumn.location },
    { title: "Skills", idName: "skills", show: showColumn.skills },
    { title: "Notes", idName: "notes", show: showColumn.notes },
    { title: "Car\xa0#", idName: "carNumber", show: showColumn.carNumber },
    {
      title: "Radio\xa0#",
      idName: "radioNumber",
      show: showColumn.radioNumber,
    },
    { title: "Timer", idName: "officerTimer", show: showColumn.officerTimer },
  ];
  const [cookies] = useCookies();
  const [tableHeight, setTableHeight] = useLocalStorage(
    `${user?.id}-available-officer-table-height`,
    windowSize.height * 0.45
  );
  // 7, 8, 89, 90, 38, S, 75, PW, 10L, T, 80, ATT
  const CODES_FOR_AVAILABLE_UNIT_STATUS = [
    "7/",
    "8/",
    "89/",
    "90/",
    "38/",
    "S/",
    "80/",
    "10L/",
    "75/",
    "PW",
    "T/",
    "ATT/",
  ];

  const nightMode = cookies[`mp-userId-${user?.id}-night-mode`] === "true";
  if (loading && onlineState && !availableOfficersCalled)
    return <p>Loading Officers</p>;
  if (error) return <p>Error: {JSON.stringify(error)}</p>;

  return (
    <>
      <div
        className={
          nightMode
            ? classes.tableH3ContainerDark
            : classes.tableH3ContainerLight
        }
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <h3
            data-cy="available-officers-title"
            className={
              nightMode ? classes.tableH3TitleDark : classes.tableH3TitleLight
            }
          >
            Available Officers
          </h3>
          &nbsp;&nbsp;
          <HelpIconButton objectname="mobile_active_officers" />
          <TableColumnShowHide
            headers={availableOfficersHeader}
            handleShowColumn={handleShowColumn}
          />
        </Box>
      </div>
      <ResizableBox
        width={dashSize.width}
        height={tableHeight}
        handle={<DragHandleIcon className={`react-resizable-handle`} />}
        handleSize={[20, 20]}
        axis={"y"}
        onResizeStop={(event, { size }) => {
          setTableHeight(size.height);
        }}
      >
        <TableContainer
          style={{ width: "100%" }}
          className={classes.tableContainer}
          component={Paper}
        >
          <Table
            stickyHeader
            className={classes.table}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow>
                {availableOfficersHeader.map(header => {
                  return (
                    <SortableTableHeader
                      show={header.show}
                      key={header.title}
                      title={header.title}
                      idName={header.idName}
                      handleSort={handleSort}
                      sortLabel={sortLabel}
                      sortedColumn={sortedColumn}
                      sortDirection={sortDirection}
                    />
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody data-cy="availOfficer-table-body">
              {rows.map((row) => {
                const backgroundColor =
                  row.status === "75" || row.status === "38"
                    ? "#fb943b"
                    : "inherit";
                row.status = row.status === "S/Loc" ? "S" : row.status;
                const allowedToEdit = canEditAvailableOfficers(
                  user,
                  row?.officerOriId,
                  row?.officerId
                );
                return (
                  <StyledTableRow
                    data-cy="availOfficer-table-row"
                    key={row.officerId || row?.id}
                    style={{ backgroundColor: backgroundColor }}
                  >
                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={{
                        display: showColumn.badge ? "" : "none",
                        fontWeight:
                          row.officerId.toString() === user?.id.toString()
                            ? "bold"
                            : undefined,
                        cursor: allowedToEdit && "pointer",
                        backgroundColor: getOriColor(
                          row.officerOriId,
                          nightMode,
                          userPreferences
                        ),
                      }}
                      scope="row"
                      onClick={() => {
                        if (allowedToEdit) {
                          toggleRadioModal();
                          setSelectedOfficer({
                            badge: row.abbreviation + "-" + row.badge,
                            id: row.officerId,
                          });
                        }
                      }}
                    >
                      {row.badge}
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={{
                        display: showColumn.ori ? "" : "none",
                        backgroundColor: getOriColor(
                          row.officerOriId,
                          nightMode,
                          userPreferences
                        ),
                      }}
                      align="left"
                    >
                      {row.abbreviation}
                    </StyledTableCell>

                    <StyledTableCell
                      className={classes.tableCellBorder}
                      // style={{ display: showColumn.assignment ? "" : 'none' }}
                      style={
                        allowedToEdit
                          ? {
                              display: showColumn.assignment ? "" : "none",
                              cursor: "pointer",
                              maxWidth: "5vw",
                              overflowWrap: "break-word",
                            }
                          : {
                              display: showColumn.assignment ? "" : "none",
                              maxWidth: "5vw",
                            }
                      }
                      onClick={() => {
                        setSelectedCell(row.officerId + "-assignment");
                      }}
                      align="left"
                    >
                      {selectedCell === row.officerId + "-assignment" &&
                      allowedToEdit ? (
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the editor opened by selecting this cell.
                        <TextField autoFocus
                          multiline={true}
                          name="assignment"
                          disabled={!allowedToEdit}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              handleSetAvailOfficer(e, row);
                            }
                            if (e.key === "Escape") {
                              setSelectedCell("");
                            }
                          }}
                          onBlur={e => handleSetAvailOfficer(e, row)}
                          defaultValue={row.assignment ? row.assignment : ""}
                          autoComplete="off"
                          fullWidth={true}
                          margin="dense"
                        />
                      ) : (
                        <p style={{ margin: "0px" }}>
                          {row.assignment}
                          {row?.loanerOriAbbreviations
                            ? "/" + row.loanerOriAbbreviations
                            : ""}
                        </p>
                      )}
                    </StyledTableCell>

                    {allowedToEdit ? (
                      <StyledTableCell
                        className={classes.tableCellBorder}
                        style={{ display: showColumn.status ? "" : "none" }}
                        align="left"
                      >
                        <Select
                          disabled={
                            isDispatcher(user?.roleNames)
                              ? false
                              : !user?.onDuty
                          }
                          id="available-officer-status-select"
                          value={
                            setup?.setupLists?.logCodes.find(lc =>
                              lc?.name?.startsWith(row.status)
                            ) || ""
                          }
                          onChange={e =>
                            handleStatusChange(e.target.value, row.officerId)
                          }
                          renderValue={value => {
                            const slashIndex = value?.name?.indexOf("/");
                            return value?.name?.substring(0, slashIndex);
                          }}
                        >
                          {setup?.setupLists?.logCodes &&
                            setup?.setupLists?.logCodes.map(logCode => {
                              const hide =
                                !CODES_FOR_AVAILABLE_UNIT_STATUS.some(el =>
                                  logCode?.name?.startsWith(el)
                                );
                              const styleObj = hide ? { display: "none" } : {};
                              return (
                                <MenuItem
                                  key={logCode.id}
                                  value={logCode}
                                  style={styleObj}
                                >
                                  {logCode.name}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </StyledTableCell>
                    ) : (
                      <StyledTableCell
                        className={classes.tableCellBorder}
                        style={{ display: showColumn.status ? "" : "none" }}
                        align="left"
                      >
                        {row.status}
                      </StyledTableCell>
                    )}
                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={
                        allowedToEdit
                          ? {
                              display: showColumn.location ? "" : "none",
                              cursor: "pointer",
                              maxWidth: "10vw",
                            }
                          : {
                              display: showColumn.location ? "" : "none",
                              maxWidth: "10vw",
                            }
                      }
                      data-cy="availOfficer-location-cell"
                      onClick={() => {
                        setSelectedCell(row.officerId + "-officerLocation");
                      }}
                      align="left"
                    >
                      {selectedCell === row.officerId + "-officerLocation" &&
                      allowedToEdit ? (
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the editor opened by selecting this cell.
                        <TextField autoFocus
                          multiline={true}
                          name="officerLocation"
                          disabled={!allowedToEdit}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              handleSetAvailOfficer(e, row);
                            }
                            if (e.key === "Escape") {
                              setSelectedCell("");
                            }
                          }}
                          onBlur={e => handleSetAvailOfficer(e, row)}
                          defaultValue={
                            row.officerLocation ? row.officerLocation : ""
                          }
                          autoComplete="off"
                          fullWidth={true}
                          margin="dense"
                        />
                      ) : (
                        <p style={{ margin: "0px" }}>
                          {row?.officerLocation?.substring(0, 128)}
                        </p>
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={{
                        display: showColumn.skills ? "" : "none",
                        maxWidth: "8vw",
                        overflowWrap: "break-word",
                      }}
                      align="left"
                    >
                      {row.skillsList}
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={
                        allowedToEdit
                          ? {
                              display: showColumn.notes ? "" : "none",
                              cursor: "pointer",
                            }
                          : {
                              display: showColumn.notes ? "" : "none",
                              overflowWrap: "break-word",
                              maxWidth: "2vw",
                            }
                      }
                      data-cy="availOfficer-notes-cell"
                      onClick={() => {
                        setSelectedCell(row.officerId + "-notes");
                      }}
                      align="left"
                    >
                      {selectedCell === row.officerId + "-notes" &&
                      allowedToEdit ? (
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the editor opened by selecting this cell.
                        <Input autoFocus
                          name="notes"
                          disabled={!allowedToEdit}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              handleSetAvailOfficer(e, row);
                            }
                            if (e.key === "Escape") {
                              setSelectedCell("");
                            }
                          }}
                          onBlur={e => handleSetAvailOfficer(e, row)}
                          defaultValue={row.notes ? row.notes : ""}
                        />
                      ) : (
                        <div>{row.notes}</div>
                      )}
                    </StyledTableCell>

                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={
                        allowedToEdit
                          ? {
                              display: showColumn.carNumber ? "" : "none",
                              cursor: "pointer",
                            }
                          : { display: showColumn.carNumber ? "" : "none" }
                      }
                      data-cy="availOfficer-car-number-cell"
                      onClick={() => {
                        setSelectedCell(row.officerId + "-carNumber");
                      }}
                      align="left"
                    >
                      {selectedCell === row.officerId + "-carNumber" &&
                      allowedToEdit ? (
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the editor opened by selecting this cell.
                        <Input autoFocus
                          name="carNumber"
                          disabled={!allowedToEdit}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              handleSetAvailOfficer(e, row);
                            }
                            if (e.key === "Escape") {
                              setSelectedCell("");
                            }
                          }}
                          onBlur={e => handleSetAvailOfficer(e, row)}
                          defaultValue={row.carNumber ? row.carNumber : ""}
                        />
                      ) : (
                        <div>{row.carNumber}</div>
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={
                        allowedToEdit
                          ? {
                              display: showColumn.radioNumber ? "" : "none",
                              cursor: "pointer",
                            }
                          : { display: showColumn.radioNumber ? "" : "none" }
                      }
                      data-cy="availOfficer-radio-number-cell"
                      onClick={() => {
                        setSelectedCell(row.officerId + "-radioNumber");
                      }}
                      align="left"
                    >
                      {selectedCell === row.officerId + "-radioNumber" &&
                      allowedToEdit ? (
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the editor opened by selecting this cell.
                        <Input autoFocus
                          name="radioNumber"
                          disabled={!allowedToEdit}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              handleSetAvailOfficer(e, row);
                            }
                            if (e.key === "Escape") {
                              setSelectedCell("");
                            }
                          }}
                          onBlur={e => handleSetAvailOfficer(e, row)}
                          defaultValue={row.radioNumber ? row.radioNumber : ""}
                        />
                      ) : (
                        <div>{row.radioNumber}</div>
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.tableCellBorder}
                      style={{
                        display: showColumn.officerTimer ? "" : "none",
                        cursor: allowedToEdit ? "pointer" : "",
                      }}
                      align="left"
                      onClick={() =>
                        handleSetLastContactedAt(row.officerId, row)
                      }
                    >
                      <Timer startTime={row.lastContactedAt} type={"officer"} />
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </ResizableBox>
      <RadioLogModal
        officer={selectedOfficer}
        modalOpen={showRadioModal}
        setModalOpen={toggleRadioModal}
        incident={null}
        incidents={null}
      />
    </>
  );
};

export default AvailableOfficerTable;
